import React, { useState, useEffect } from 'react';
import { apiService } from '../services';
import { useEntityStats } from '../hooks/admin/useEntityStats';
import { useDashboard } from '../hooks/admin/useDashboard';
import { useItemHandlers } from '../hooks/admin/useItemHandlers';
import {
  EntityTypeSelector,
  FilterBar,
  EntityGrid,
  AlbumColumn,
  CreateModal,
  EditModal,
  MessagesPanel,
} from '../components/admin';
import { Article, Event, Album, Book, Canticle } from '../types';

type SelectedType = 'articles' | 'events' | 'albums' | 'canticles' | 'books';
type View = 'content' | 'messages';

export const AdminDashboardPage: React.FC = () => {
  const [view, setView] = useState<View>('content');
  const [selectedType, setSelectedType] = useState<SelectedType>('articles');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<
    Article | Event | Album | Book | Canticle | null
  >(null);

  // Stats hook for entity counts
  const { stats, loading: statsLoading, refresh: refreshStats } = useEntityStats();

  // Unified dashboard hook (combines entity list + album editor)
  const {
    items,
    loading: listLoading,
    filters,
    pagination,
    updateFilters,
    clearFilters,
    goToPage,
    refresh: refreshList,
    // Album-specific (only used when selectedType === 'albums')
    mediaItems,
    albumLoading,
    albumError,
    handleAddMedia,
    handleDeleteMedia,
    handleUpdateAlbum,
    setAlbumError,
  } = useDashboard({
    selectedType,
    editingAlbumId: editingItem && selectedType === 'albums' ? editingItem.id : null,
    onSuccess: async () => {
      await Promise.all([refreshStats(), refreshList()]);
    },
  });

  // Item handlers for CRUD operations
  const {
    handleCreate,
    handleUpdate,
    handleDelete,
    formLoading,
    formError,
    setFormError,
  } = useItemHandlers({
    onSuccess: async () => {
      await Promise.all([refreshStats(), refreshList()]);
    },
  });

  // Refresh data on mount and when type changes
  useEffect(() => {
    refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  const handleTypeChange = (type: SelectedType) => {
    setSelectedType(type);
    setEditingItem(null);
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setFormError(null);
    setAlbumError(null);
  };

  const handleCreateClick = () => {
    setCreateModalOpen(true);
    setFormError(null);
    setAlbumError(null);
  };

  const handleEditClick = async (item: Article | Event | Album | Book | Canticle) => {
    try {
      // Fetch full item data
      let fullItem;
      switch (selectedType) {
        case 'articles':
          fullItem = await apiService.getArticle(item.id);
          break;
        case 'events':
          fullItem = await apiService.getEvent(item.id);
          break;
        case 'albums':
          fullItem = await apiService.getAlbum(item.id);
          break;
        case 'canticles':
          fullItem = await apiService.getCanticle(item.id);
          break;
        case 'books':
          fullItem = await apiService.getBook(item.id);
          break;
        default:
          return;
      }
      setEditingItem(fullItem);
      setEditModalOpen(true);
      setFormError(null);
      setAlbumError(null);
    } catch (err) {
      console.error('Failed to fetch item:', err);
      setFormError('Erreur lors du chargement de l\'élément');
    }
  };

  const handleDeleteClick = async (item: Article | Event | Album | Book | Canticle) => {
    await handleDelete(selectedType, item.id);
  };

  const handleCreateSubmit = async (
    type: SelectedType,
    data: any,
    files?: File | FileList
  ): Promise<Article | Event | Album | Book | Canticle | void> => {
    const result = await handleCreate(type, data, files);
    setCreateModalOpen(false);
    return result;
  };

  const handleUpdateSubmit = async (
    id: number,
    type: SelectedType,
    data: any,
    files?: File | FileList
  ) => {
    await handleUpdate(id, type, data, files);
    setEditModalOpen(false);
    setEditingItem(null);
  };

  const handleAlbumUpdate = async (data: any) => {
    if (editingItem && selectedType === 'albums') {
      await handleUpdateAlbum(data);
      setEditModalOpen(false);
      setEditingItem(null);
    }
  };

  return (
    <div className='min-h-screen'>
      <div className='max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8'>

        {/* View Toggle */}
        <div className='flex gap-3 mb-6'>
          <button
            onClick={() => setView('content')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              view === 'content'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-500 hover:text-blue-600'
            }`}
          >
            Contenu
          </button>
          <button
            onClick={() => setView('messages')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              view === 'messages'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-500 hover:text-blue-600'
            }`}
          >
            Messages
          </button>
        </div>

        {view === 'messages' ? (
          <MessagesPanel />
        ) : (
          <>
            {/* Entity Type Selector */}
            <EntityTypeSelector
              selectedType={selectedType}
              onTypeChange={handleTypeChange}
              stats={stats}
              loading={statsLoading}
            />

            {/* Filter Bar */}
            <FilterBar
              type={selectedType}
              filters={filters}
              onFiltersChange={updateFilters}
              onClearFilters={clearFilters}
              onCreateClick={handleCreateClick}
            />

            {/* Entity Display */}
            {selectedType === 'albums' ? (
              <AlbumColumn
                albums={items as Album[]}
                loading={listLoading}
                pagination={pagination}
                onPageChange={goToPage}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
              />
            ) : (
              <EntityGrid
                items={items}
                type={selectedType}
                loading={listLoading}
                pagination={pagination}
                onPageChange={goToPage}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
              />
            )}

            {/* Create Modal */}
            <CreateModal
              isOpen={createModalOpen}
              onClose={() => {
                setCreateModalOpen(false);
                setFormError(null);
              }}
              type={selectedType}
              loading={formLoading}
              error={formError}
              onSubmit={handleCreateSubmit}
            />

            {/* Edit Modal */}
            {editingItem && (
              <EditModal
                isOpen={editModalOpen}
                onClose={() => {
                  setEditModalOpen(false);
                  setEditingItem(null);
                  setFormError(null);
                  setAlbumError(null);
                }}
                type={selectedType}
                item={editingItem}
                loading={formLoading || (selectedType === 'albums' && albumLoading)}
                error={formError || (selectedType === 'albums' ? albumError : null)}
                onUpdate={handleUpdateSubmit}
                onAlbumUpdate={selectedType === 'albums' ? handleAlbumUpdate : undefined}
                onAlbumAddMedia={
                  selectedType === 'albums' ? handleAddMedia : undefined
                }
                onAlbumDeleteMedia={
                  selectedType === 'albums' ? handleDeleteMedia : undefined
                }
                albumMediaItems={selectedType === 'albums' ? mediaItems : undefined}
                albumLoading={selectedType === 'albums' ? albumLoading : false}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
